import { getISOWeek, getISOWeekYear, getDay, addDays } from 'date-fns';

export interface WeekInfo {
  week: number;
  year: number;
}

/**
 * Hämtar aktuell vecka och år enligt ISO-8601 standard
 * - Veckan börjar på måndag
 * - Vecka 1 är den vecka som innehåller årets första torsdag
 */
export const getCurrentWeekInfo = (): WeekInfo => {
  const now = new Date();
  return {
    week: getISOWeek(now),
    year: getISOWeekYear(now),
  };
};

/**
 * Hämtar aktuell veckodag (1-7, måndag=1, söndag=7) enligt ISO-standard
 */
export const getCurrentISODayOfWeek = (): number => {
  const jsDay = getDay(new Date()); // 0=söndag, 1=måndag, ...
  return jsDay === 0 ? 7 : jsDay; // Konvertera till ISO: 1=måndag, 7=söndag
};

/**
 * Returnerar svenskt dagsnamn baserat på ISO-veckodag (1-7)
 */
export const getSwedishDayName = (isoDay: number): string => {
  const dayNames: Record<number, string> = {
    1: 'Måndag',
    2: 'Tisdag',
    3: 'Onsdag',
    4: 'Torsdag',
    5: 'Fredag',
    6: 'Lördag',
    7: 'Söndag',
  };
  return dayNames[isoDay] || '';
};

/**
 * Hämtar dagens namn på svenska
 */
export const getCurrentDayName = (): string => {
  return getSwedishDayName(getCurrentISODayOfWeek());
};

/**
 * Kontrollerar om det är en vardag (måndag-fredag)
 */
export const isWeekday = (): boolean => {
  const day = getCurrentISODayOfWeek();
  return day >= 1 && day <= 5;
};

/**
 * Returnerar det effektiva datumet för lunchvisning.
 * På vardagar: dagens datum
 * På helger: kommande måndag
 */
export const getEffectiveLunchDate = (): Date => {
  const now = new Date();
  const isoDay = getDay(now) === 0 ? 7 : getDay(now); // 1=måndag, 7=söndag
  
  if (isoDay <= 5) {
    // Måndag-fredag: returnera dagens datum
    return now;
  } else if (isoDay === 6) {
    // Lördag: lägg till 2 dagar för att nå måndag
    return addDays(now, 2);
  } else {
    // Söndag: lägg till 1 dag för att nå måndag
    return addDays(now, 1);
  }
};

/**
 * Hämtar veckoinfo för det effektiva lunchdatumet
 */
export const getEffectiveLunchWeekInfo = (): WeekInfo => {
  const effectiveDate = getEffectiveLunchDate();
  return {
    week: getISOWeek(effectiveDate),
    year: getISOWeekYear(effectiveDate),
  };
};

/**
 * Hämtar veckodag för det effektiva lunchdatumet (alltid 1=måndag på helger)
 */
export const getEffectiveLunchDayName = (): string => {
  const effectiveDate = getEffectiveLunchDate();
  const jsDay = getDay(effectiveDate);
  const isoDay = jsDay === 0 ? 7 : jsDay;
  return getSwedishDayName(isoDay);
};

/**
 * Kontrollerar om vi visar nästa veckas lunch (dvs. det är helg)
 */
export const isShowingNextMondayLunch = (): boolean => {
  const isoDay = getCurrentISODayOfWeek();
  return isoDay >= 6; // Lördag (6) eller söndag (7)
};

/**
 * Returnerar veckor att visa i CMS (föregående, nuvarande, kommande)
 */
export const getWeeksForCMS = (): WeekInfo[] => {
  const current = getCurrentWeekInfo();
  const weeks: WeekInfo[] = [];
  
  // Beräkna föregående vecka
  const prevWeek = current.week === 1 ? 52 : current.week - 1;
  const prevYear = current.week === 1 ? current.year - 1 : current.year;
  weeks.push({ week: prevWeek, year: prevYear });
  
  // Nuvarande vecka
  weeks.push(current);
  
  // Kommande veckor
  for (let i = 1; i <= 2; i++) {
    const nextWeek = ((current.week - 1 + i) % 52) + 1;
    const nextYear = current.week + i > 52 ? current.year + 1 : current.year;
    weeks.push({ week: nextWeek, year: nextYear });
  }
  
  return weeks;
};

/**
 * Formaterar vecka och år för visning
 */
export const formatWeekDisplay = (week: number, year: number): string => {
  return `V.${week} (${year})`;
};

/**
 * Jämför två WeekInfo-objekt
 */
export const isSameWeek = (a: WeekInfo, b: WeekInfo): boolean => {
  return a.week === b.week && a.year === b.year;
};
